import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { DataTreeItem, GatewaySensorType } from '../../services/api';
import { getSensorsById } from '../../state/selectors';
import { useAppSelector } from '../../state/store';
import { ListOptions } from '../../Views/OfflineView';
import OfflineLocSummary from './OfflineLocSummary';
import OfflineSensorSummary from './OfflineSensorSummary';
import OfflineGatewaySummary from './OfflineGatewaySummary';

// Helper Component: Renders the list of sub-locations when a parent location is only partially offline.
function PartialOfflineLocation({
  offlineSensors,
  locSensors,
  excludeLocations, // fully offline locations
  listType,
  handleTagging,
}: {
  offlineSensors: DataTreeItem[];
  locSensors: DataTreeItem[];
  excludeLocations: Set<string>;
  listType: ListOptions;
  handleTagging: (sensors: DataTreeItem[], isTagged: boolean) => void;
}) {
  const sensorsById = useAppSelector(getSensorsById);

  // Identify unique sub-locations that have offline sensors, excluding fully offline parents
  const validSubLocations = useMemo(() => {
    const locations = offlineSensors
      .map((item) => item.location)
      .filter((loc): loc is string => !!loc && !excludeLocations.has(loc));
    return Array.from(new Set(locations));
  }, [offlineSensors, excludeLocations]);

  if (validSubLocations.length === 0) return null;

  return (
    <Box>
      {validSubLocations.map((subLocation) => {
        const subLocOfflineSensors = offlineSensors.filter((item) => item.location === subLocation);
        const allSubLocSensors = locSensors.filter((item) => item.location === subLocation);

        if (allSubLocSensors.length === 0) return null;

        // Identify offline gateways
        const offlineGateways = subLocOfflineSensors
          .filter((sensor) => sensorsById.get(sensor.id)?.type === GatewaySensorType.LfWiFiSensor)
          .map((s) => s.id);

        // Filter faulty sensors (exclude those whose gateway is already offline to avoid double reporting)
        const filteredFaultySensors = subLocOfflineSensors.filter((sensor) => {
          const gateway = sensorsById.get(sensor.id)?.gateway;
          return gateway && !offlineGateways.includes(gateway);
        });

        const isLocFullyOffline = allSubLocSensors.length === subLocOfflineSensors.length;

        return (
          <OfflineLocSummary
            key={subLocation}
            locId={subLocation}
            listType={listType}
            locSensors={allSubLocSensors}
            offlineSensors={subLocOfflineSensors}
            handleTagging={handleTagging}
          >
            <Box>
              <OfflineGatewaySummary
                allSensors={allSubLocSensors}
                isLocOffline={isLocFullyOffline}
                listType={listType}
                handleTagging={handleTagging}
                offlineGateways={offlineGateways}
              />
              <OfflineSensorSummary
                faultySensors={filteredFaultySensors}
                listType={listType}
                handleTagging={handleTagging}
              />
            </Box>
          </OfflineLocSummary>
        );
      })}
    </Box>
  );
}

export default PartialOfflineLocation;
